import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBooking } from '../contexts/BookingContext';
import { purchaseService } from '../services/purchaseService';
import { productService } from '../services/productService';
import { authService } from '../services/authService';
import type { Purchase, PurchaseStatus, ListPurchasesParams } from '../types/purchase';
import type { Product } from '../types/product';

export const PurchaseManagement: React.FC = () => {
  const { t } = useTranslation();
  const { businessProfile } = useBooking();

  // State
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 20;

  // Filters
  const [statusFilter, setStatusFilter] = useState<PurchaseStatus | ''>('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [searchType, setSearchType] = useState<'name' | 'email'>('name');

  // Mark used
  const [markUsedModalOpen, setMarkUsedModalOpen] = useState(false);
  const [markUsedPurchase, setMarkUsedPurchase] = useState<Purchase | null>(null);
  const [markingUsed, setMarkingUsed] = useState(false);
  const [markUsedError, setMarkUsedError] = useState<string | null>(null);

  // Extend modal
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState<Purchase | null>(null);
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [extendNotes, setExtendNotes] = useState('');
  const [isExtending, setIsExtending] = useState(false);
  const [extendError, setExtendError] = useState<string | null>(null);
  const [extendSuccess, setExtendSuccess] = useState<string | null>(null);

  // Issue comp modal
  const [issueCompModalOpen, setIssueCompModalOpen] = useState(false);
  const [issueCompProducts, setIssueCompProducts] = useState<Product[]>([]);
  const [issueCompProductId, setIssueCompProductId] = useState('');
  const [issueCompEmail, setIssueCompEmail] = useState('');
  const [issueCompName, setIssueCompName] = useState('');
  const [issueCompPhone, setIssueCompPhone] = useState('');
  const [issueCompReason, setIssueCompReason] = useState('');
  const [issueCompSendEmail, setIssueCompSendEmail] = useState(true);
  const [issuingComp, setIssuingComp] = useState(false);
  const [issueCompError, setIssueCompError] = useState<string | null>(null);

  const fetchPurchases = useCallback(async () => {
    if (!businessProfile?.id) return;

    setIsLoading(true);
    setError(null);

    try {
      const params: ListPurchasesParams = {
        page: currentPage,
        pageSize,
      };

      if (statusFilter) {
        params.status = statusFilter;
      }

      if (customerSearch) {
        if (searchType === 'name') {
          params.customerName = customerSearch;
        } else {
          params.customerEmail = customerSearch;
        }
      }

      const response = await purchaseService.listBusinessPurchases(businessProfile.id, params) as any;
      // Handle the API response structure
      setPurchases(response.purchases || response.items || []);
      setTotalCount(response.totalCount || 0);
      setTotalPages(response.totalPages || 1);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('purchases.fetchError');
      setError(errorMessage);
      console.error('Failed to fetch purchases:', err);
    } finally {
      setIsLoading(false);
    }
  }, [businessProfile?.id, currentPage, statusFilter, customerSearch, searchType, t]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchPurchases();
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setCustomerSearch('');
    setCurrentPage(1);
  };

  const openExtendModal = (purchase: Purchase) => {
    setSelectedPurchase(purchase);
    // Set default new expiry to 30 days from current expiry
    const currentExpiry = new Date(purchase.expiryDate);
    currentExpiry.setDate(currentExpiry.getDate() + 30);
    setNewExpiryDate(currentExpiry.toISOString().split('T')[0]);
    setExtendNotes('');
    setExtendError(null);
    setExtendSuccess(null);
    setExtendModalOpen(true);
  };

  const openMarkUsedModal = (purchase: Purchase) => {
    setMarkUsedPurchase(purchase);
    setMarkUsedError(null);
    setMarkUsedModalOpen(true);
  };

  const handleMarkAsUsed = async () => {
    if (!markUsedPurchase) return;

    setMarkingUsed(true);
    setMarkUsedError(null);

    try {
      await purchaseService.markPurchaseAsUsed(markUsedPurchase.id);
      setMarkUsedModalOpen(false);
      fetchPurchases();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('purchases.markUsedError');
      setMarkUsedError(errorMessage);
    } finally {
      setMarkingUsed(false);
    }
  };

  const handleExtendPurchase = async () => {
    if (!selectedPurchase || !newExpiryDate) return;

    setIsExtending(true);
    setExtendError(null);
    setExtendSuccess(null);

    try {
      const response = await purchaseService.extendPurchaseExpiry(selectedPurchase.id, {
        newExpiryDate: `${newExpiryDate}T23:59:59Z`,
        notes: extendNotes || undefined,
      });

      setExtendSuccess(response.message || t('purchases.extendSuccess'));

      // Refresh the list after a short delay
      setTimeout(() => {
        setExtendModalOpen(false);
        fetchPurchases();
      }, 1500);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('purchases.extendError');
      setExtendError(errorMessage);
    } finally {
      setIsExtending(false);
    }
  };

  const getStatusColor = (status: PurchaseStatus) => {
    switch (status) {
      case 'active':
        return 'bg-brand-confirm/30 text-brand-confirm-deep dark:text-brand-confirm border-brand-confirm/30';
      case 'depleted':
        return 'bg-brand-accent/20 text-brand-accent border-brand-accent/30';
      case 'expired':
        return 'bg-brand-warm/30 text-brand-warm-deep dark:text-brand-warm border-brand-warm/30';
      case 'pending_payment':
        return 'bg-brand-warm/15 text-brand-warm-deep dark:text-brand-warm border-brand-warm/30';
      default:
        return 'bg-surface-app-elevated text-ink-secondary border-border-strong/30';
    }
  };

  // Turn backend snake_case statuses ("pending_payment") into the camelCase
  // i18n keys we use in the locale files ("statusPendingPayment").
  const statusI18nKey = (status: string) =>
    `purchases.status${status
      .split('_')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join('')}`;

  const isStaleAbandonedPending = (purchase: Purchase) => {
    if (purchase.status !== 'pending_payment') return false;
    const created = purchase.createdAt ?? purchase.purchaseDate;
    if (!created) return false;
    const ageMs = Date.now() - new Date(created).getTime();
    return ageMs >= 24 * 60 * 60 * 1000;
  };

  const openIssueCompModal = async () => {
    if (!businessProfile?.id) return;
    setIssueCompError(null);
    setIssueCompProductId('');
    setIssueCompEmail('');
    setIssueCompName('');
    setIssueCompPhone('');
    setIssueCompReason('');
    setIssueCompSendEmail(true);
    setIssueCompModalOpen(true);
    try {
      const token = authService.storage.getToken();
      if (!token) return;
      const products = await productService.getProducts(businessProfile.id, token);
      setIssueCompProducts(products.filter((p) => p.isActive));
    } catch (err) {
      setIssueCompError(err instanceof Error ? err.message : t('purchases.issueCompError'));
    }
  };

  const handleIssueComp = async () => {
    if (!businessProfile?.id) return;
    if (!issueCompProductId || !issueCompEmail.trim() || issueCompReason.trim().length < 3) {
      setIssueCompError(t('purchases.issueCompValidation'));
      return;
    }
    setIssuingComp(true);
    setIssueCompError(null);
    try {
      await purchaseService.issueComp(businessProfile.id, {
        productId: issueCompProductId,
        customerEmail: issueCompEmail.trim(),
        customerName: issueCompName.trim() || undefined,
        customerPhone: issueCompPhone.trim() || undefined,
        reason: issueCompReason.trim(),
        sendEmail: issueCompSendEmail,
      });
      setIssueCompModalOpen(false);
      fetchPurchases();
    } catch (err) {
      setIssueCompError(err instanceof Error ? err.message : t('purchases.issueCompError'));
    } finally {
      setIssuingComp(false);
    }
  };

  const handleDeletePending = async (purchase: Purchase) => {
    if (!confirm(t('purchases.deletePendingConfirm'))) return;
    try {
      await purchaseService.deleteStalePending(purchase.id);
      fetchPurchases();
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('purchases.deletePendingError');
      setError(msg);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pl-PL');
  };

  if (!businessProfile) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-ink-secondary">{t('purchases.noBusinessProfile')}</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-surface-app">
      {/* Header */}
      <div className="p-6 border-b border-border-subtle">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-2xl font-bold text-ink-primary">{t('purchases.title')}</h2>
            <button
              onClick={openIssueCompModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-accent hover:bg-brand-accent-hover text-white rounded-lg transition-colors text-base font-semibold shadow-sm hover:shadow"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {t('purchases.issueComp')}
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-4 flex flex-wrap gap-4 items-end">
          {/* Status Filter */}
          <div>
            <label className="block text-sm text-ink-secondary mb-1">{t('purchases.status')}</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as PurchaseStatus | '')}
              className="px-3 py-2 bg-surface-app-elevated border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value="">{t('purchases.allStatuses')}</option>
              <option value="active">{t('purchases.statusActive')}</option>
              <option value="depleted">{t('purchases.statusDepleted')}</option>
              <option value="expired">{t('purchases.statusExpired')}</option>
            </select>
          </div>

          {/* Customer Search */}
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm text-ink-secondary mb-1">{t('purchases.searchCustomer')}</label>
            <div className="flex gap-2">
              <select
                value={searchType}
                onChange={(e) => setSearchType(e.target.value as 'name' | 'email')}
                className="px-3 py-2 bg-surface-app-elevated border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
              >
                <option value="name">{t('purchases.byName')}</option>
                <option value="email">{t('purchases.byEmail')}</option>
              </select>
              <input
                type="text"
                value={customerSearch}
                onChange={(e) => setCustomerSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                placeholder={searchType === 'name' ? t('purchases.namePlaceholder') : t('purchases.emailPlaceholder')}
                className="flex-1 px-3 py-2 bg-surface-app-elevated border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleSearch}
              className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg transition-colors"
            >
              {t('purchases.search')}
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-surface-card border border-border-strong hover:bg-surface-card-hover text-ink-primary rounded-lg transition-colors"
            >
              {t('purchases.clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {error && (
          <div className="mb-4 p-4 bg-brand-warm/10 dark:bg-brand-warm/20 border border-brand-warm/30 rounded-lg">
            <p className="text-brand-warm-deep dark:text-brand-warm">{error}</p>
          </div>
        )}

        {markUsedError && (
          <div className="mb-4 p-4 bg-brand-warm/10 dark:bg-brand-warm/20 border border-brand-warm/30 rounded-lg">
            <p className="text-brand-warm-deep dark:text-brand-warm">{markUsedError}</p>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-ink-secondary">{t('purchases.loading')}</div>
          </div>
        ) : purchases.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-ink-secondary">{t('purchases.noPurchases')}</div>
          </div>
        ) : (
          <>
            {/* Results count */}
            <div className="mb-4 text-sm text-ink-secondary">
              {t('purchases.showing', { count: purchases.length, total: totalCount })}
            </div>

            {/* Table */}
            <div className="bg-surface-card rounded-lg border border-border-subtle overflow-hidden">
              <table className="w-full">
                <thead className="bg-surface-app-elevated">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-ink-primary">{t('purchases.code')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-ink-primary">{t('purchases.customer')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-ink-primary">{t('purchases.product')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-ink-primary">{t('purchases.entries')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-ink-primary">{t('purchases.purchaseDate')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-ink-primary">{t('purchases.expiryDate')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-ink-primary">{t('purchases.status')}</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-ink-primary">{t('purchases.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {purchases.map((purchase) => (
                    <tr key={purchase.id} className="hover:bg-surface-app-elevated">
                      <td className="px-4 py-3">
                        <span className="text-ink-primary font-mono text-sm">{purchase.id.substring(0, 8).toUpperCase()}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-ink-primary font-medium">{purchase.customerName || '-'}</p>
                          <p className="text-sm text-ink-secondary">{purchase.customerEmail}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-ink-primary">{purchase.productTitle}</td>
                      <td className="px-4 py-3">
                        <span className="text-ink-primary">{purchase.remainingUsage}</span>
                        <span className="text-ink-secondary">/{purchase.totalUsage}</span>
                      </td>
                      <td className="px-4 py-3 text-ink-primary">{formatDate(purchase.purchaseDate)}</td>
                      <td className="px-4 py-3 text-ink-primary">{formatDate(purchase.expiryDate)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(purchase.status)}`}>
                            {t(statusI18nKey(purchase.status))}
                          </span>
                          {purchase.issuedByUserId && (
                            <span
                              className="px-2 py-1 text-xs font-mono font-semibold rounded-full border bg-brand-primary/10 text-brand-primary border-brand-primary/30 uppercase tracking-wider"
                              title={purchase.issuedReason ?? t('purchases.localSaleBadgeHint')}
                            >
                              {t('purchases.localSaleBadge')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          {purchase.status === 'active' && purchase.remainingUsage > 0 && (
                            <button
                              onClick={() => openMarkUsedModal(purchase)}
                              className="px-3 py-1 text-sm bg-brand-confirm hover:bg-brand-confirm-deep text-white rounded transition-colors"
                            >
                              {t('purchases.markUsed')}
                            </button>
                          )}
                          {purchase.status !== 'pending_payment' && (
                            <button
                              onClick={() => openExtendModal(purchase)}
                              className="px-3 py-1 text-sm bg-brand-primary hover:bg-brand-primary-hover text-white rounded transition-colors"
                            >
                              {t('purchases.extend')}
                            </button>
                          )}
                          {isStaleAbandonedPending(purchase) && (
                            <button
                              onClick={() => handleDeletePending(purchase)}
                              className="px-3 py-1 text-sm bg-brand-warm hover:bg-brand-warm-deep text-white rounded transition-colors"
                              title={t('purchases.deletePendingHint')}
                            >
                              {t('purchases.deletePending')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-4 flex items-center justify-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 bg-surface-card border border-border-subtle text-ink-primary rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-card-hover transition-colors"
                >
                  {t('purchases.previous')}
                </button>
                <span className="text-ink-secondary">
                  {t('purchases.pageOf', { current: currentPage, total: totalPages })}
                </span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 bg-surface-card border border-border-subtle text-ink-primary rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-surface-card-hover transition-colors"
                >
                  {t('purchases.next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Mark Used Confirmation Modal */}
      {markUsedModalOpen && markUsedPurchase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-primary-deep rounded-lg shadow-xl w-full max-w-sm">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">{t('purchases.markUsedTitle')}</h3>
                <button
                  onClick={() => setMarkUsedModalOpen(false)}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-white/90">{t('purchases.markUsedConfirm')}</p>

              <div className="p-4 bg-surface-card rounded-lg">
                <p className="text-sm text-ink-secondary">{t('purchases.customer')}</p>
                <p className="text-ink-primary font-medium">{markUsedPurchase.customerName || markUsedPurchase.customerEmail}</p>
                <p className="text-sm text-ink-secondary mt-2">{t('purchases.product')}</p>
                <p className="text-ink-primary">{markUsedPurchase.productTitle}</p>
                <p className="text-sm text-ink-secondary mt-2">{t('purchases.entries')}</p>
                <p className="text-ink-primary">
                  {markUsedPurchase.remainingUsage - 1}
                  <span className="text-ink-secondary">/{markUsedPurchase.totalUsage}</span>
                  <span className="text-ink-secondary text-sm ml-2">({t('purchases.markUsedAfter')})</span>
                </p>
              </div>

              {markUsedError && (
                <div className="p-3 bg-brand-warm/20 border border-brand-warm/40 rounded-lg">
                  <p className="text-sm text-brand-warm">{markUsedError}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={() => setMarkUsedModalOpen(false)}
                className="px-4 py-2 bg-white/10 border border-white/30 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                {t('purchases.cancel')}
              </button>
              <button
                onClick={handleMarkAsUsed}
                disabled={markingUsed}
                className="px-4 py-2 bg-brand-confirm hover:bg-brand-confirm-deep text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {markingUsed ? t('purchases.markingUsed') : t('purchases.confirmMarkUsed')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Modal */}
      {extendModalOpen && selectedPurchase && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-brand-primary-deep rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">{t('purchases.extendTitle')}</h3>
                <button
                  onClick={() => setExtendModalOpen(false)}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Purchase Info */}
              <div className="p-4 bg-surface-card rounded-lg">
                <p className="text-sm text-ink-secondary">{t('purchases.customer')}</p>
                <p className="text-ink-primary font-medium">{selectedPurchase.customerName || selectedPurchase.customerEmail}</p>
                <p className="text-sm text-ink-secondary mt-2">{t('purchases.product')}</p>
                <p className="text-ink-primary">{selectedPurchase.productTitle}</p>
                <p className="text-sm text-ink-secondary mt-2">{t('purchases.currentExpiry')}</p>
                <p className="text-ink-primary">{formatDate(selectedPurchase.expiryDate)}</p>
              </div>

              {/* New Expiry Date */}
              <div>
                <label className="block text-sm text-white/70 mb-1">{t('purchases.newExpiryDate')}</label>
                <input
                  type="date"
                  value={newExpiryDate}
                  onChange={(e) => setNewExpiryDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm text-white/70 mb-1">{t('purchases.notes')}</label>
                <textarea
                  value={extendNotes}
                  onChange={(e) => setExtendNotes(e.target.value)}
                  placeholder={t('purchases.notesPlaceholder')}
                  rows={3}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary"
                />
              </div>

              {/* Error/Success Messages */}
              {extendError && (
                <div className="p-3 bg-brand-warm/20 border border-brand-warm/40 rounded-lg">
                  <p className="text-sm text-brand-warm">{extendError}</p>
                </div>
              )}

              {extendSuccess && (
                <div className="p-3 bg-brand-confirm/20 border border-brand-confirm/40 rounded-lg">
                  <p className="text-sm text-brand-confirm">{extendSuccess}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-white/10 flex justify-end gap-3">
              <button
                onClick={() => setExtendModalOpen(false)}
                className="px-4 py-2 bg-white/10 border border-white/30 hover:bg-white/20 text-white rounded-lg transition-colors"
              >
                {t('purchases.cancel')}
              </button>
              <button
                onClick={handleExtendPurchase}
                disabled={isExtending || !newExpiryDate}
                className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isExtending ? t('purchases.extending') : t('purchases.confirmExtend')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue Comp Modal — light theme per design-system spec */}
      {issueCompModalOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-surface-card rounded-2xl border border-border-subtle shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-border-subtle">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-ink-primary tracking-tight">{t('purchases.issueCompTitle')}</h3>
                <button
                  onClick={() => setIssueCompModalOpen(false)}
                  className="text-ink-muted hover:text-ink-primary transition-colors"
                  aria-label={t('purchases.cancel')}
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-ink-secondary mt-2">{t('purchases.issueCompHint')}</p>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">{t('purchases.issueCompProduct')}</label>
                <select
                  value={issueCompProductId}
                  onChange={(e) => setIssueCompProductId(e.target.value)}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                >
                  {/* CSS variables on <option> so the native dropdown popup resolves
                      surface + ink against the active theme — not against the OS
                      chrome (which would render light-on-light in dark mode). */}
                  <option
                    value=""
                    disabled
                    style={{ color: 'var(--color-ink-muted)', backgroundColor: 'var(--color-surface-card)' }}
                  >
                    {t('purchases.issueCompProductPlaceholder')}
                  </option>
                  {issueCompProducts.map((p) => (
                    <option
                      key={p.id}
                      value={p.id}
                      style={{ color: 'var(--color-ink-primary)', backgroundColor: 'var(--color-surface-card)' }}
                    >
                      {p.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">{t('purchases.issueCompEmail')}</label>
                <input
                  type="email"
                  value={issueCompEmail}
                  onChange={(e) => setIssueCompEmail(e.target.value)}
                  placeholder="adres@example.com"
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-ink-secondary mb-1">{t('purchases.issueCompName')}</label>
                  <input
                    type="text"
                    value={issueCompName}
                    onChange={(e) => setIssueCompName(e.target.value)}
                    className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-secondary mb-1">{t('purchases.issueCompPhone')}</label>
                  <input
                    type="tel"
                    value={issueCompPhone}
                    onChange={(e) => setIssueCompPhone(e.target.value)}
                    className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-secondary mb-1">
                  {t('purchases.issueCompReason')} <span className="text-brand-warm">*</span>
                </label>
                <textarea
                  value={issueCompReason}
                  onChange={(e) => setIssueCompReason(e.target.value)}
                  rows={2}
                  placeholder={t('purchases.issueCompReasonPlaceholder')}
                  className="w-full px-3 py-2 bg-surface-card border border-border-strong rounded-lg text-ink-primary placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                />
                <p className="text-xs text-ink-muted mt-1">{t('purchases.issueCompReasonHelp')}</p>
              </div>

              <label className="flex items-center gap-2 text-sm text-ink-primary cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={issueCompSendEmail}
                  onChange={(e) => setIssueCompSendEmail(e.target.checked)}
                  className="w-4 h-4 accent-brand-primary"
                />
                {t('purchases.issueCompSendEmail')}
              </label>

              {issueCompError && (
                <div className="p-3 bg-brand-warm/10 border border-brand-warm/30 rounded-lg">
                  <p className="text-sm text-brand-warm-deep">{issueCompError}</p>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-border-subtle flex justify-end gap-3">
              <button
                onClick={() => setIssueCompModalOpen(false)}
                className="px-4 py-2 bg-surface-card border border-border-strong text-ink-primary hover:bg-surface-card-hover rounded-lg transition-colors"
              >
                {t('purchases.cancel')}
              </button>
              <button
                onClick={handleIssueComp}
                disabled={issuingComp || !issueCompProductId || !issueCompEmail || issueCompReason.trim().length < 3}
                className="px-4 py-2 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {issuingComp ? t('purchases.issueCompSubmitting') : t('purchases.issueCompSubmit')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
